import React, {useCallback, useState, useImperativeHandle, forwardRef} from 'react';
import styles from './UserData.module.css';
import cx from 'classnames';
import Input from '../../../components/Input';
import {useOnboardingContext} from '../../../contexts/OnboardingContext';
import {
    validateAllUserDataFields,
    UserDataFieldErrors
} from '../../../helpers/validationHelper';

interface UserDataStepProps {
    isActive?: boolean;
}

export interface UserDataStepRef {
    validate: () => boolean;
}

const UserDataStep = forwardRef<UserDataStepRef, UserDataStepProps>(({isActive = false}, ref) => {
    const {formData, setUserDataField} = useOnboardingContext();
    const [errors, setErrors] = useState<UserDataFieldErrors>({});

    useImperativeHandle(ref, () => ({
        validate: () => {
            const validationErrors = validateAllUserDataFields(formData.userData);
            setErrors(validationErrors);
            return Object.keys(validationErrors).length === 0;
        }
    }));

    const handleInputChange = useCallback((property: string, value: string) => {
        if (property === 'postalCode') {
            value = value.replace(/\D/g, '');
        }

        setUserDataField(property, value);

        if (errors[property as keyof UserDataFieldErrors]) {
            setErrors(prev => {
                const newErrors = {...prev};
                delete newErrors[property as keyof UserDataFieldErrors];
                return newErrors;
            });
        }
    }, [setUserDataField, errors]);

    return (
        <div className={cx(styles['user-data-step-container'])} hidden={!isActive}>
            <h2 className={styles['step-title']}>Firmendaten</h2>
            <div className={styles['step-content']}>
                <div className={styles['inputs-grid']}>
                    <div className={styles['input-wrapper']}>
                        <Input
                            type="text"
                            placeholder="Firma"
                            property="company"
                            value={formData.userData.company}
                            onChange={handleInputChange}
                            classes={styles['input-field']}
                        />
                        {errors.company && (
                            <span className={styles['error-message']}>{errors.company}</span>
                        )}
                    </div>
                    <div className={styles['input-wrapper']}>
                        <Input
                            type="text"
                            placeholder="Vorname"
                            property="firstName"
                            value={formData.userData.firstName}
                            onChange={handleInputChange}
                            classes={styles['input-field']}
                        />
                        {errors.firstName && (
                            <span className={styles['error-message']}>{errors.firstName}</span>
                        )}
                    </div>
                    <div className={styles['input-wrapper']}>
                        <Input
                            type="text"
                            placeholder="Nachname"
                            property="lastName"
                            value={formData.userData.lastName}
                            onChange={handleInputChange}
                            classes={styles['input-field']}
                        />
                        {errors.lastName && (
                            <span className={styles['error-message']}>{errors.lastName}</span>
                        )}
                    </div>
                    <div className={styles['input-wrapper']}>
                        <Input
                            type="text"
                            placeholder="Straße"
                            property="street"
                            value={formData.userData.street}
                            onChange={handleInputChange}
                            classes={styles['input-field']}
                        />
                        {errors.street && (
                            <span className={styles['error-message']}>{errors.street}</span>
                        )}
                    </div>
                    <div className={styles['input-wrapper']}>
                        <Input
                            type="text"
                            placeholder="Hausnummer"
                            property="houseNumber"
                            value={formData.userData.houseNumber}
                            onChange={handleInputChange}
                            classes={styles['input-field']}
                        />
                    </div>
                    <div className={styles['input-wrapper']}>
                        <Input
                            type="text"
                            placeholder="Postleitzahl"
                            property="postalCode"
                            value={formData.userData.postalCode}
                            onChange={handleInputChange}
                            classes={styles['input-field']}
                            maxLength={5}
                        />
                        {errors.postalCode && (
                            <span className={styles['error-message']}>{errors.postalCode}</span>
                        )}
                    </div>
                    <div className={styles['input-wrapper']}>
                        <Input
                            type="text"
                            placeholder="Ort"
                            property="city"
                            value={formData.userData.city}
                            onChange={handleInputChange}
                            classes={styles['input-field']}
                        />
                        {errors.city && (
                            <span className={styles['error-message']}>{errors.city}</span>
                        )}
                    </div>
                    <div className={styles['input-wrapper']}>
                        <Input
                            type="email"
                            placeholder="Email"
                            property="email"
                            value={formData.userData.email}
                            onChange={handleInputChange}
                            classes={styles['input-field']}
                        />
                        {errors.email && (
                            <span className={styles['error-message']}>{errors.email}</span>
                        )}
                    </div>
                    <div className={styles['input-wrapper']}>
                        <Input
                            type="text"
                            placeholder="Telefonnummer"
                            property="phoneNumber"
                            value={formData.userData.phoneNumber}
                            onChange={handleInputChange}
                            classes={styles['input-field']}
                        />
                        {errors.phoneNumber && (
                            <span className={styles['error-message']}>{errors.phoneNumber}</span>
                        )}
                    </div>
                    <div className={styles['input-wrapper']}>
                        <Input
                            type="number"
                            placeholder="Anzahl Mitarbeiter"
                            property="memberCount"
                            value={formData.userData.memberCount}
                            onChange={handleInputChange}
                            classes={styles['input-field']}
                        />
                        {errors.memberCount && (
                            <span className={styles['error-message']}>{errors.memberCount}</span>
                        )}
                    </div>
                    <div className={styles['input-wrapper']}>
                        <Input
                            type="textarea"
                            placeholder="Zusätzliche Informationen"
                            property="additionalInformation"
                            value={formData.userData.additionalInformation}
                            onChange={handleInputChange}
                            classes={styles['textarea-field']}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
});

UserDataStep.displayName = 'UserDataStep';

export default UserDataStep;


